import {
  Button,
  Flex,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Grid,
  Input,
  PhoneInput,
  Text,
} from "@suns/design-system";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { LoaderCircle, MailIcon, PhoneIcon } from "lucide-react";
import { toast } from "@/shared/utils/toaster";
import { ToastType } from "@/shared/utils/toaster";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useState } from "react";
import { usePrompt } from "../Prompt/Prompt";
import { SunsApiError } from "@suns/api";
import { useNavigateLogin } from "@/shared/hooks";

interface ContactInfoProps {
  contactInfo: ContactInfo;
  onSubmit: (contactInfo: ContactInfo) => Promise<ContactInfo>;
}

export interface ContactInfo {
  id: number;
  email: string | null;
  mobilePhone: string | null;
  workPhone: string | null;
}

export function ContactInfoView({ contactInfo, onSubmit }: ContactInfoProps) {
  const navigateLogin = useNavigateLogin();
  const [contactInfoResponse, setContactInfoResponse] =
    useState<ContactInfo | null>(null);
  const [saving, setSaving] = useState(false);
  const prompt = usePrompt();

  const handleEmailClick = (email: string) => {
    window.open(`mailto:${email}`);
  };

  const handlePhoneClick = (phone: string) => {
    window.open(`tel:${phone}`);
  };

  const handleClickUpsert = async (personnelFormData: ContactInfo) => {
    const confirmation = await prompt(
      "Are you sure you want to update this contact information?",
      ""
    );

    if (confirmation) {
      try {
        setSaving(true);
        const updatedContactInfo = await onSubmit(personnelFormData);

        setContactInfoResponse(updatedContactInfo);

        toast(ToastType.SUCCESS, "Contact information updated");
      } catch (error) {
        if (error instanceof SunsApiError && error.authError) {
          toast(ToastType.ERROR, "Please log back in to update.");
          navigateLogin();
        }
        toast(
          ToastType.ERROR,
          "Unable to update contact information. Please try again."
        );
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <>
      <Flex gap="md">
        <Button
          className="max-sm:w-1/3"
          onClick={() =>
            handlePhoneClick(
              contactInfoResponse?.mobilePhone ?? contactInfo.mobilePhone ?? ""
            )
          }
        >
          <PhoneIcon className="mr-2 size-4" />
          <Text size="sm">Mobile</Text>
        </Button>
        <Button
          variant="muted"
          className="max-sm:w-1/3"
          onClick={() =>
            handlePhoneClick(
              contactInfoResponse?.workPhone ?? contactInfo.workPhone ?? ""
            )
          }
        >
          <PhoneIcon className="mr-2 size-4" />
          <Text size="sm">Work</Text>
        </Button>
        <Button
          variant="muted"
          className="max-sm:w-1/3"
          onClick={() =>
            handleEmailClick(
              contactInfoResponse?.email ?? contactInfo.email ?? ""
            )
          }
        >
          <MailIcon className="mr-2 size-4" />
          <Text size="sm">Email</Text>
        </Button>
      </Flex>
      <ContactInfoForm
        contactInfo={contactInfoResponse ?? contactInfo}
        saving={saving}
        onUpsert={handleClickUpsert}
      />
    </>
  );
}

interface ContactInfoFormProps {
  contactInfo: ContactInfo;
  saving: boolean;
  onUpsert: (values: ContactInfo) => void;
}

function ContactInfoForm({
  contactInfo,
  onUpsert,
  saving,
}: ContactInfoFormProps) {
  const formSchema = yup.object({
    email: yup.string().email().nullable(),
    mobilePhone: yup
      .string()
      .test({
        test: (value) => (value ? isValidPhoneNumber(value) : true),
      })
      .nullable(),
    workPhone: yup
      .string()
      .test({
        test: (value) => (value ? isValidPhoneNumber(value) : true),
      })
      .nullable(),
  });

  type FormSchema = yup.InferType<typeof formSchema>;

  const form = useForm<FormSchema>({
    defaultValues: contactInfo,
    mode: "onChange",
  });

  const handleSave = async () => {
    const { email, mobilePhone, workPhone } = form.getValues();
    try {
      await formSchema.validate(
        {
          email,
          mobilePhone: mobilePhone,
          workPhone: workPhone,
        },
        {
          abortEarly: false,
        }
      );
      onUpsert({
        ...contactInfo,
        email: email ?? null,
        mobilePhone: mobilePhone ?? null,
        workPhone: workPhone ?? null,
      });
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        error.inner.forEach((e) => {
          if (e.path) {
            form.setError(e.path as keyof FormSchema, {
              type: "custom",
              message: e.message,
            });
          }
        });
        toast(ToastType.ERROR, "Unable to update agent. Please try again.");
      }
    }
  };

  return (
    <Form {...form}>
      <form className="flex flex-col items-end gap-4">
        <Grid columns="3" gap="sm" className="max-md:grid-cols-1">
          <FormField
            name="mobilePhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Mobile Phone</FormLabel>
                <FormControl>
                  <PhoneInput
                    placeholder="Enter a phone number"
                    {...field}
                    value={field.value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="workPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Work Phone</FormLabel>
                <FormControl>
                  <PhoneInput placeholder="Enter a phone number" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Email</FormLabel>
                <FormControl>
                  <Input
                    className="text-[16px]"
                    {...field}
                    value={field.value}
                    type="email"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </Grid>
        <div>
          <Flex gap="sm">
            <Button type="button" onClick={handleSave}>
              {saving ? (
                <Flex align="center" gap="sm">
                  Updating...
                  <LoaderCircle size={18} className="animate-spin" />
                </Flex>
              ) : (
                "Update"
              )}
            </Button>
            <Button
              variant="muted"
              type="button"
              onClick={() => form.reset(contactInfo)}
            >
              Cancel
            </Button>
          </Flex>
        </div>
      </form>
    </Form>
  );
}
